// A split candidate's condition, described the same way a tree node's own is.
//
// rgip sends a candidate structured (`column`, `operator`, `values`) alongside a
// `condition` string it assembled for a human. That string is not SQL: literals are
// unquoted and it says "is not" where SQL says "<>", so it cannot go through the
// parser, and printed raw it showed the customer `company__category__industry_gr...`.
//
// Rather than restate conditionText's wording here, the structured fields are put
// back into the SQL they came from and handed to conditionTraits. The whole point of
// this module is that a candidate and a node describe a condition identically, and a
// second implementation of the same English is exactly how the two drift apart.
import {
  ColumnLabels,
  conditionTraits,
  ConditionTrait,
  TraitPart,
} from '../trees/conditionText';
import { labelOf } from './computationDictionary';

/**
 * The parts of a split candidate this needs. Structural rather than the API type,
 * so the renderer does not depend on the transport shape.
 */
export interface SplitCandidateCondition {
  column?: string;
  operator?: string;
  values?: (string | number)[];
  /**
   * The run said as a range, on an ordinal candidate only: "4-pre-2010 and above".
   *
   * A band is a span of a scale, and listing its levels makes the reader work out for
   * themselves that they are consecutive. `it spend bucket in ZERO or MED or HIGH or
   * VHI` is four levels of one scale wearing the grammar of an arbitrary set.
   */
  band?: string;
  /**
   * How rgip's engine read the column. `single_value` is emitted only where a record
   * carries SEVERAL values (a technology array, a comma-joined tag list), because the
   * engine can only test one bucket at a time there.
   *
   * It is load-bearing rather than descriptive. On a multi-valued column the bucket
   * `apache_http_server` is one ELEMENT of the array the column holds, never the whole
   * value, so `= 'apache_http_server'` matches nothing and `<> 'apache_http_server'`
   * matches everything. That is what put 0 leads and 0 conversions on one side of an
   * applied split. Membership has to be tested with `contains`.
   */
  kind?: string;
  /**
   * The column as a person would say it: "HQ Country", "IT spend", "B2B enterprise".
   *
   * rgip sends it out of the label dictionary it already keeps in sync with studio-cli,
   * which is where that vocabulary is maintained and why Studio does not carry a second
   * copy. Absent on deliverables cached before rgip started sending it, and then the
   * name derived from `column` stands in, which is what showed a customer "it spend
   * bucket" and "tag is b2b enterprise".
   */
  label?: string;
  /**
   * The agent's sentence for this split: "Companies that do not use Apache HTTP Server".
   *
   * Prose, written by the model and merged onto the candidate by id server-side. It
   * replaces the description this module builds, and nothing else: what a click applies
   * is still assembled from `column`, `operator`, `values` and `kind` in
   * `candidateSplit`, which never reads this field.
   *
   * Absent on every candidate past the third of a node, which the panel does not show,
   * and on every deliverable cached before it existed.
   */
  phrase?: string;
  /** rgip's own human string, all a deliverable cached before the fields existed has. */
  condition?: string;
  /**
   * The columns a cut over SEVERAL of them tests, in the order they are ANDed.
   *
   * Only the clean-companies root sends this, and it is why `column` can be absent: the
   * cut is one split over the four lead-quality flags rather than a test on one field.
   */
  columns?: string[];
  /**
   * Per column, the value that column stores for a JUNK record.
   *
   * Not one shared value, because the flags do not agree: `is_spam`, `is_edu` and
   * `is_student` store "1" while `is_personal` stores "Personal email". A cut written
   * with the wrong one matches no record, and a predicate matching no record on the
   * negative side of a root selects every record.
   */
  junk_values?: Record<string, string>;
}

// The kind rgip marks a cut over several columns with. Exported because `candidateSplit`
// discriminates on the same string, and two copies of it is how the description and the
// write start disagreeing about which candidate this is.
export const CLEAN_ROOT_KIND = 'clean_root';

// rgip's four operator names, mapped to the SQL that means the same thing. "is not"
// is its wording for <>; the other three are already SQL.
const SQL_OPERATOR: Record<string, string> = {
  in: 'IN',
  '>=': '>=',
  '=': '=',
  'is not': '<>',
};

// A comparison that orders its operands is on a number, so a numeric string is
// emitted unquoted: quoted, conditionTraits reads it as text and "employees 5000 or
// more" loses the thousands separator the same node condition would have had.
// Equality and IN are left quoted, where a numeric-looking value can genuinely be a
// code rather than a quantity.
function literal(value: string | number, ordered: boolean): string {
  if (typeof value === 'number') return String(value);
  const trimmed = value.trim();
  if (ordered && trimmed !== '' && Number.isFinite(Number(trimmed))) {
    return trimmed;
  }
  return `'${value.replace(/'/g, "''")}'`;
}

/**
 * A column name as a person would say it, matching how conditionText renders the
 * field half of a node's own condition: separators become spaces and the prefixes
 * Studio adds are dropped.
 */
function humanColumn(column: string): string {
  return column
    .replace(/^(raw__|hg__|company__|predictleads__)/, '')
    .replace(/__/g, ' ')
    .replace(/_/g, ' ')
    .trim();
}

/**
 * rgip's label for this candidate's column, else the dictionary's, else a derived name.
 *
 * The dictionary is the same table rgip labels out of, so the middle step is not a second
 * copy of the vocabulary: it is the same one, read locally, which is what lets a candidate
 * cached before rgip sent labels still read as "IT Spend" rather than "it spend bucket".
 * `humanColumn` remains the last resort for a tenant's own computation, which nothing has
 * written a label for.
 */
function columnLabel(candidate: SplitCandidateCondition): string {
  const supplied = candidate.label?.trim();
  const column = candidate.column ?? '';
  return supplied || labelOf(column) || humanColumn(column);
}

/**
 * The label in the form conditionTraits takes, or nothing when none is known.
 *
 * Still nothing rather than a DERIVED name: conditionTraits derives the same name itself,
 * and passing one here would claim a label was supplied when it was not. A dictionary
 * label is not derived, so it is passed: it is the reviewed vocabulary, and withholding it
 * would leave the SQL path reading "it spend bucket" while the band path reads "IT Spend".
 */
function columnLabels(
  candidate: SplitCandidateCondition
): ColumnLabels | undefined {
  if (!candidate.column) return undefined;
  const known = candidate.label?.trim() || labelOf(candidate.column);
  if (!known) return undefined;
  return { [candidate.column]: known };
}

// The same operators on a column where one record carries SEVERAL values. There a
// bucket is one element of the array the column holds, so equality is not the test:
// `candidateSplit` applies `contains` / `does not contain` for exactly this reason, and
// describing it as "tech other than apache_http_server" told the user something other
// than what the button would do. LIKE is what conditionText reads as containment, and
// what Studio's own technology computations are written with.
const LIKE_OPERATOR: Record<string, string> = {
  '=': 'LIKE',
  'is not': 'NOT LIKE',
};

// rgip emits this kind only from its multi-value branch. Named the same way and for the
// same reason as `candidateSplit`'s copy, which is the file that decides what a click
// writes: the description and the write have to read the same flag.
const MULTI_VALUE_KIND = 'single_value';

function candidateSql(candidate: SplitCandidateCondition): string | null {
  const { column, operator, values } = candidate;
  const sqlOperator = operator ? SQL_OPERATOR[operator] : undefined;
  if (!column || !sqlOperator || !values?.length) return null;
  const value = String(values[0]);
  // A `%` in the value would be read as a wildcard of this module's own making and
  // stripped back off for display, so those keep the plain comparison.
  const likeOperator =
    candidate.kind === MULTI_VALUE_KIND && operator && !value.includes('%')
      ? LIKE_OPERATOR[operator]
      : undefined;
  if (likeOperator) {
    return `${column} ${likeOperator} '%${value.replace(/'/g, "''")}%'`;
  }
  const ordered = sqlOperator === '>=';
  const literals = values.map((item) => literal(item, ordered));
  return sqlOperator === 'IN'
    ? `${column} IN (${literals.join(', ')})`
    : `${column} ${sqlOperator} ${literals[0]}`;
}

// The parser hands a string literal back exactly as it was written, doubled quote
// included, so a value escaped on the way in comes out as "Moody''s". Undoubling is
// the exact inverse of the escape above, and only a literal this module quoted can
// carry a doubled quote: a value taken from a column name never does.
function unescapeValues(trait: ConditionTrait): ConditionTrait {
  const parts: TraitPart[] = trait.parts.map((part) =>
    part.kind === 'value'
      ? { kind: part.kind, text: part.text.replace(/''/g, "'") }
      : part
  );
  // Rebuilt the way conditionText builds its own text, so the two stay the same
  // string for the same parts.
  const text = parts
    .map((part) => part.text)
    .filter(Boolean)
    .join(' ')
    .trim();
  return { ...trait, parts, text };
}

/**
 * One trait describing the candidate, for the same TraitText the node panel uses.
 *
 * Falls back to rgip's own string, flagged `unparsed` the way conditionTraits flags
 * SQL it cannot read, when the structured fields are missing or unreadable. Returns
 * null only when there is nothing at all to show.
 */
export function candidateConditionTrait(
  candidate: SplitCandidateCondition
): ConditionTrait | null {
  // The agent's sentence wins where there is one. It is written from the same structured
  // fields, in business language this module cannot reach: it can say "IT spend very
  // high and below" where the fields give "IT spend: VHI and below", because expanding a
  // coded value and naming what kind of company a bucket describes is not derivable from
  // the column and the value.
  //
  // One `word` part, not `field` and `value`: a sentence has no field half to set apart,
  // and splitting one on a guess would mark the wrong words. Not `unparsed` either, which
  // means "this is raw SQL nobody could read" and styles it as the failure it is.
  const phrase = candidate.phrase?.trim();
  if (phrase) {
    return { text: phrase, parts: [{ kind: 'word', text: phrase }] };
  }
  // A cut over several columns has no field half and no single value, so there is no
  // structured description to build: it is one idea ("set the junk leads aside") that
  // happens to compile to four predicates. rgip authors the sentence, and this shows it
  // the same way the `phrase` branch above does.
  //
  // NOT flagged `unparsed`. That marker means "this is raw SQL nobody could read" and is
  // styled as the failure it is, which would be wrong for a sentence written for a
  // marketer.
  if (candidate.kind === CLEAN_ROOT_KIND) {
    const text = candidate.condition?.trim();
    if (!text) return null;
    return { text, parts: [{ kind: 'word', text }] };
  }
  // A band is described by its span, not by its members. Built here rather than
  // through conditionTraits, because there is no SQL whose natural English is "and
  // above": the range is the engine's own summary of a contiguous run.
  if (candidate.band && candidate.column) {
    const field = columnLabel(candidate);
    // The colon is how a node's own condition introduces a value ("company type:
    // Private"), and a band is a value. Without it "IT spend VHI and below" runs the
    // field and the range together as one phrase.
    return {
      text: `${field}: ${candidate.band}`,
      parts: [
        { kind: 'field', text: `${field}:` },
        { kind: 'value', text: candidate.band },
      ],
    };
  }
  const sql = candidateSql(candidate);
  const traits = sql ? conditionTraits(sql, columnLabels(candidate)) : [];
  // Exactly one trait is expected: none of the four operators can produce an AND, and
  // the one candidate that does compile to an AND is handled above by kind. Anything
  // else means the SQL was not what this built, so the raw string is the honest thing
  // to show rather than half a description.
  if (traits.length === 1 && !traits[0].unparsed)
    return unescapeValues(traits[0]);
  const raw = candidate.condition?.trim();
  if (!raw) return null;
  return { text: raw, parts: [{ kind: 'word', text: raw }], unparsed: true };
}
